import React, { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";

export const shouldCloseSidebarAfterNavigation = (viewportWidth) =>
  viewportWidth <= 1024;

export const getSidebarMenuAction = (viewportWidth, isCollapsed) => {
  if (shouldCloseSidebarAfterNavigation(viewportWidth)) return "open";
  if (isCollapsed) return "expand";
  return "none";
};

const WORKSPACE_ITEMS = {
  admin: [
    { id: "cashier", label: "Cashier", shortLabel: "CA", path: "/cashier" },
    { id: "admin", label: "Admin", shortLabel: "AD", path: "/admin" },
  ],
  cashier: [
    { id: "cashier", label: "Cashier", shortLabel: "CA", path: "/cashier" },
  ],
};

const NAVIGATION_GROUPS = {
  admin: [
    {
      id: "overview",
      label: "Overview",
      items: [
        { path: "/admin", label: "Dashboard", shortLabel: "DB", view: "dashboard" },
      ],
    },
    {
      id: "operations",
      label: "Operations",
      items: [
        { path: "/admin", label: "Inventory", shortLabel: "ST", view: "inventory" },
        { path: "/admin", label: "Purchases", shortLabel: "PU", view: "purchases" },
        { path: "/admin", label: "Suppliers", shortLabel: "SP", view: "suppliers" },
      ],
    },
    {
      id: "insights",
      label: "Insights",
      items: [
        { path: "/admin", label: "Reports", shortLabel: "RP", view: "reports" },
      ],
    },
  ],
  cashier: [
    {
      id: "operations",
      label: "Operations",
      items: [
        { path: "/cashier", label: "Sales Counter", shortLabel: "SC" },
        { path: "/receipts", label: "Receipts", shortLabel: "RC" },
      ],
    },
    {
      id: "stock-tools",
      label: "Stock tools",
      items: [
        { path: "/quick-edit", label: "Quick Edit", shortLabel: "QE" },
      ],
    },
  ],
};

export const getActiveWorkspace = (pathname = "") =>
  pathname.startsWith("/admin") ? "admin" : "cashier";

export const getWorkspaceItems = (role) =>
  WORKSPACE_ITEMS[role === "admin" ? "admin" : "cashier"];

export const getNavigationGroups = (workspace) =>
  NAVIGATION_GROUPS[workspace === "admin" ? "admin" : "cashier"];

const LogoutIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M10 3H5.8C5.35817 3 5 3.35817 5 3.8V20.2C5 20.6418 5.35817 21 5.8 21H10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13 8L19 12L13 16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 12H19"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function Sidebar({
  user,
  onLogout,
  currentView,
  onViewChange,
  isOpen,
  onClose,
  isCollapsed,
  onToggleCollapse,
  menuButtonRef,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme, uiScale, setUiScale } = useTheme();
  const sidebarRef = useRef(null);

  const isActive = (item) => {
    if (currentView && item.view) return currentView === item.view;
    return location.pathname === item.path;
  };


  const activeWorkspace = getActiveWorkspace(location.pathname);

  const restoreMenuFocus = useCallback(() => {
    const restore = () => menuButtonRef?.current?.focus();
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(restore);
    } else {
      setTimeout(restore, 0);
    }
  }, [menuButtonRef]);

  const closeSidebar = useCallback(() => {
    onClose?.();
    restoreMenuFocus();
  }, [onClose, restoreMenuFocus]);

  useEffect(() => {
    if (!isOpen || !sidebarRef.current) return undefined;
    const drawer = sidebarRef.current;
    const getFocusable = () =>
      Array.from(
        drawer.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
    getFocusable()[0]?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeSidebar();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = getFocusable();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeSidebar, isOpen]);

  const handleWorkspaceClick = (workspace) => {
    navigate(workspace.path);
    if (shouldCloseSidebarAfterNavigation(window.innerWidth)) {
      closeSidebar();
    }
  };

  const handleNavClick = (item) => {
    if (onViewChange) {
      onViewChange(item.view);
    } else {
      navigate(item.path);
    }
    // Close the overlay sidebar after navigation on phones and tablets.
    if (shouldCloseSidebarAfterNavigation(window.innerWidth) && onClose) {
      closeSidebar();
    }
  };

  const userName =
    user?.fullName || user?.full_name || user?.username || "Signed-in user";
  const workspaceLabel = activeWorkspace === "admin" ? "Admin" : "Cashier";
  const stationLabel =
    activeWorkspace === "admin" ? "Back office" : "Front counter";

  return (
    <>
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}
      <aside
        ref={sidebarRef}
        id="app-sidebar"
        className={`sidebar ${isOpen ? "open" : ""} ${isCollapsed ? "collapsed" : ""}`}
        aria-label="Application navigation"
      >
        <div className="workspace-rail">
          <div className="workspace-brand">
            <img src="/images/zawkhinLogo.PNG" alt="Zaw Khin Market POS" />
          </div>
          <nav className="workspace-switcher" aria-label="Workspaces">
            {getWorkspaceItems(user?.role).map((workspace) => (
              <button
                type="button"
                key={workspace.id}
                data-workspace={workspace.id}
                className={`workspace-switcher-item ${
                  activeWorkspace === workspace.id ? "active" : ""
                }`}
                aria-label={`${workspace.label} workspace`}
                aria-current={
                  activeWorkspace === workspace.id ? "page" : undefined
                }
                title={workspace.label}
                onClick={() => handleWorkspaceClick(workspace)}
              >
                <span aria-hidden="true">{workspace.shortLabel}</span>
              </button>
            ))}
          </nav>
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-controls="sidebar-context-panel"
            aria-expanded={!isCollapsed}
            aria-label={
              isCollapsed ? "Expand navigation" : "Collapse navigation"
            }
            title={isCollapsed ? "Expand navigation" : "Collapse navigation"}
          >
            <span aria-hidden="true">{isCollapsed ? ">" : "<"}</span>
          </button>
          <div className="workspace-rail-spacer" />
          <div className="workspace-user" title={userName}>
            <span aria-hidden="true">{userName.charAt(0).toUpperCase()}</span>
            <span className="sr-only">Signed in as {userName}</span>
          </div>
        </div>

        <div id="sidebar-context-panel" className="sidebar-context-panel">
          <header className="sidebar-context-header">
            <div>
              <span className="sidebar-workspace-kicker">
                {workspaceLabel} workspace
              </span>
              <strong>{stationLabel}</strong>
            </div>
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={closeSidebar}
              aria-label="Close menu"
            >
              ✕
            </button>
          </header>

          <nav
            className="sidebar-nav"
            aria-label={`${workspaceLabel} navigation`}
          >
            {getNavigationGroups(activeWorkspace).map((group) => (
              <section
                className="sidebar-nav-group"
                key={group.id}
                aria-labelledby={`sidebar-nav-${group.id}`}
              >
                <span
                  id={`sidebar-nav-${group.id}`}
                  className="sidebar-nav-group-label"
                >
                  {group.label}
                </span>
                {group.items.map((item) => (
                  <button
                    type="button"
                    key={item.view || item.path}
                    className={`sidebar-nav-item ${
                      isActive(item) ? "active" : ""
                    }`}
                    onClick={() => handleNavClick(item)}
                    aria-current={isActive(item) ? "page" : undefined}
                  >
                    <span
                      className="nav-icon nav-icon-badge"
                      aria-hidden="true"
                    >
                      {item.shortLabel}
                    </span>
                    <span className="nav-label">{item.label}</span>
                  </button>
                ))}
              </section>
            ))}
          </nav>

          <footer className="sidebar-footer">
            <div className="sidebar-status-card">
              <span className="sidebar-status-label">Current station</span>
              <strong className="sidebar-status-value">{stationLabel}</strong>
              <span className="sidebar-status-meta">
                <span className="sidebar-status-dot" aria-hidden="true" />
                System ready
              </span>
            </div>
            <div className="sidebar-scale-block">
              <span className="sidebar-scale-label">Text size</span>
              <div
                className="sidebar-scale-options"
                role="group"
                aria-label="Text size"
              >
                {["compact", "normal", "large"].map((scale, index) => (
                  <button
                    type="button"
                    key={scale}
                    className={`sidebar-scale-btn ${
                      uiScale === scale ? "active" : ""
                    }`}
                    aria-pressed={uiScale === scale}
                    aria-label={`${scale} text size`}
                    onClick={() => setUiScale(scale)}
                  >
                    {["S", "M", "L"][index]}
                  </button>
                ))}
              </div>
            </div>
            <div className="sidebar-action-stack">
              <button
                type="button"
                className="sidebar-theme-btn"
                onClick={toggleTheme}
                aria-label={
                  theme === "light"
                    ? "Switch to dark mode"
                    : "Switch to light mode"
                }
              >
                <span className="sidebar-theme-icon" aria-hidden="true">
                  {theme === "light" ? "MOON" : "SUN"}
                </span>
                <span className="sidebar-theme-copy">
                  <strong>
                    {theme === "light" ? "Night view" : "Day view"}
                  </strong>
                </span>
              </button>
              <button
                type="button"
                className="sidebar-logout-btn"
                onClick={onLogout}
                aria-label="Logout"
              >
                <span aria-hidden="true">
                  <LogoutIcon />
                </span>
              </button>
            </div>
          </footer>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;

